import { flatten, get, orderBy, uniqBy } from "lodash-es";

import { getLocaleText } from "shared/boot/i18n";
import { sentimentOptions } from "shared/components/core/pickers/SentimentPicker";
import { getSource } from "shared/helpers/media";
import ModalService from "shared/modals/ModalService";
import OrganisationReportService from "shared/modules/OrganisationReport/services/OrganisationReportService";
import {
  type OrganisationReportFolder,
  type OrganisationReportSummaryType,
} from "shared/modules/OrganisationReport/types";
import { isOrganisationReportMention } from "shared/modules/OrganisationReport/types/guards";
import ApiClient, { ApiClientError } from "shared/services/api/ApiClient";
import features from "shared/services/features";
import type { Stream } from "shared/types";
import {
  type Excerpts,
  type Mention,
  MENTION_TYPE_LONG_TO_SHORT,
  MentionType,
  type Sentiment,
  type ShortMentionType,
  type Syndication,
  type SyndicationKey,
} from "shared/types/mentions";
import {
  isMentionFinancialTimes,
  isMentionWithRelatedArticle,
  isMentionWithSyndications,
} from "shared/types/mentions/guards";

import { isNumber } from "./number";

export enum SortOptionField {
  ADVERTISEMENT_RATE = "advertisement_rate",
  AUDIENCE = "audience",
  AUTHOR = "author.raw",
  AUTHOR_FIELD = "author",
  COMMENT_COUNT = "comment_count",
  DOMAIN_AUTHORITY = "domain_authority",
  ENGAGEMENT_COUNT = "engagement_count",
  ENRICHMENT_EMOTIONALITY = "risk_enrichment.aggregate_emotionality",
  ENRICHMENT_FAKENESS = "risk_enrichment.aggregate_fakeness",
  ENRICHMENT_HARMFUL = "risk_enrichment.aggregate_harmful",
  ENRICHMENT_SCORE = "risk_enrichment.risk_score",
  ENRICHMENT_SPAM = "risk_enrichment.aggregate_spam",
  FOLLOWER_COUNT = "follower_count",
  IMPACT_SCORE = "impact_score",
  LIKE_COUNT = "like_count",
  SENTIMENT = "sentiment",
  SOCIAL_AUTHOR = "author_sort",
  SOURCE_NAME = "source.name",
  TIMESTAMP = "timestamp",
  WORD_COUNT = "word_count",
}

export enum SortOptionKey {
  IMPACT_SCORE = "impact_score.score",
}

export interface SortOption {
  field: SortOptionField;
  key: SortOptionField | SortOptionKey;
  label: string;
}

export enum SortOptionOrder {
  ASC = "asc",
  DESC = "desc",
}

export enum SortOptionMissing {
  FIRST = "_first",
  LAST = "_last",
}

export interface MentionSortOption {
  missing: SortOptionMissing;
  sort_by: SortOptionField;
  sort_order: SortOptionOrder;
}

interface MentionOptions {
  sort_options: MentionSortOption[];
}

export interface OrderOption {
  field: SortOptionOrder;
  label: string;
}

export interface CompositeIdToMention {
  id: number;
  type: MentionType;
}

function getSortOptions(): SortOption[] {
  return [
    {
      field: SortOptionField.TIMESTAMP,
      key: SortOptionField.TIMESTAMP,
      label: getLocaleText("helpers.mentions.sort_options.timestamp"),
    },
    {
      field: SortOptionField.AUTHOR,
      key: SortOptionField.AUTHOR,
      label: getLocaleText("helpers.mentions.sort_options.journalist_author"),
    },
    {
      field: SortOptionField.SOURCE_NAME,
      key: SortOptionField.SOURCE_NAME,
      label: getLocaleText("helpers.mentions.sort_options.source"),
    },
    {
      field: SortOptionField.WORD_COUNT,
      key: SortOptionField.WORD_COUNT,
      label: getLocaleText("helpers.mentions.sort_options.word_count"),
    },
    {
      field: SortOptionField.AUDIENCE,
      key: SortOptionField.AUDIENCE,
      label: getLocaleText("helpers.mentions.sort_options.audience"),
    },
    {
      field: SortOptionField.SENTIMENT,
      key: SortOptionField.SENTIMENT,
      label: getLocaleText("helpers.mentions.sort_options.sentiment"),
    },
    {
      field: SortOptionField.IMPACT_SCORE,
      key: SortOptionKey.IMPACT_SCORE,
      label: getLocaleText("helpers.mentions.sort_options.impact_score"),
    },
    {
      field: SortOptionField.DOMAIN_AUTHORITY,
      key: SortOptionField.DOMAIN_AUTHORITY,
      label: getLocaleText("helpers.mentions.sort_options.domain_authority"),
    },
  ];
}

function getOrderOptions(): OrderOption[] {
  return [
    {
      field: SortOptionOrder.DESC,
      label: getLocaleText("helpers.mentions.order_options.desc"),
    },
    {
      field: SortOptionOrder.ASC,
      label: getLocaleText("helpers.mentions.order_options.asc"),
    },
  ];
}

const excludedMediaItemSortFields: SortOptionField[] = [
  SortOptionField.AUTHOR,
  SortOptionField.WORD_COUNT,
  SortOptionField.AUDIENCE,
];

const excludedTranscriptRequestSortFields = [
  SortOptionField.AUTHOR,
  SortOptionField.SOURCE_NAME,
  SortOptionField.WORD_COUNT,
  SortOptionField.AUDIENCE,
];

function sortingKey<T = Mention>(
  sortField: SortOptionField
): (mention: T) => string | number {
  let sortKey: string = sortField;
  const excludedWords = ["the"];
  const regex = new RegExp(excludedWords.join("|"));

  let coerceToNumber = false;

  if (sortField === SortOptionField.AUDIENCE) coerceToNumber = true;

  if (sortField === SortOptionField.IMPACT_SCORE) {
    sortKey = SortOptionKey.IMPACT_SCORE;
  }

  return (mention: T): string | number => {
    const field: unknown = get(mention, sortKey, 0);

    if (coerceToNumber) {
      return field ? parseInt(String(field), 10) : 0;
    }

    if (typeof field === "string") {
      return field.toLowerCase().replace(regex, "").trim();
    }

    return Number(field) || 0;
  };
}

function sortMentions<T = Mention>(
  mentions: T[],
  options: MentionOptions = { sort_options: [] }
): T[] {
  const sortFields = options.sort_options.map((opt) => opt.sort_by);
  const sortOrders = options.sort_options.map((opt) => opt.sort_order);
  const functions = sortFields.map((sortKey) => sortingKey<T>(sortKey));

  return orderBy(mentions, functions, sortOrders);
}

function processMentions(
  mentions: Mention[],
  options: MentionOptions = { sort_options: [] }
): Mention[] {
  return sortMentions(mentions, options);
}

function normalizedKeywords(keywords: string[] = []): string[] {
  return uniqBy(keywords, (keyword) => keyword.toLowerCase());
}

function excerptsKeywords(excerpts: Excerpts[] = []): string[] {
  return normalizedKeywords(
    flatten(excerpts.map(({ keywords }) => keywords || []))
  );
}

function sortedSyndications(mention: Mention): Syndication[] {
  if (!isMentionWithSyndications(mention)) {
    return [];
  }

  return mention.syndications.slice().sort((syndicationA, syndicationB) => {
    const nameA = syndicationA.source_name ?? "";
    const nameB = syndicationB.source_name ?? "";

    return nameA > nameB ? 1 : -1;
  });
}

function mentionKeywords(mention: Mention): string[] {
  if (Array.isArray(mention.keywords)) return mention.keywords;

  if (
    mention.keywords &&
    typeof mention.keywords === "object" &&
    mention.keywords.constructor === Object
  ) {
    return Object.keys(mention.keywords);
  }

  if (mention.excerpts && mention.excerpts.length) {
    return excerptsKeywords(mention.excerpts);
  }

  return [];
}

function resolveShortMentionType(
  type: MentionType | "caption"
): ShortMentionType {
  if (type === "caption") {
    return MENTION_TYPE_LONG_TO_SHORT[MentionType.tv_caption];
  }

  return MENTION_TYPE_LONG_TO_SHORT[type];
}

function compoundId(mention: Pick<Mention, "id" | "type">): string {
  return `${resolveShortMentionType(mention.type)}${mention.id}`;
}

function targetType(mention: Pick<Mention, "id" | "type">): string {
  return mention.type === "external_item"
    ? "ExternalItem"
    : getSource(mention.type).relatedCamelCaseField || "";
}

function compositeIdToMention(
  compositeId: SyndicationKey
): CompositeIdToMention {
  const mention = compositeId.split("_");

  return {
    id: Number(mention.at(-1)),
    type: mention.slice(0, -1).join("_") as MentionType,
  };
}

function formattedMedium(mention: Mention) {
  return mention.medium?.toLowerCase() ?? "";
}

function getSortPossibilities(): SortOption[] {
  const possibilities = [
    {
      options: Array.from(getSortOptions()),
    },
  ];

  const defaultGroupOptions = possibilities[0].options;

  if (features.has("advertising_value_mentions")) {
    defaultGroupOptions.push({
      field: SortOptionField.ADVERTISEMENT_RATE,
      key: SortOptionField.ADVERTISEMENT_RATE,
      label: getLocaleText("helpers.mentions.sort_options.advertisement_rate"),
    });
  }

  if (features.has("has_react_score")) {
    defaultGroupOptions.push({
      field: SortOptionField.ENRICHMENT_SCORE,
      key: SortOptionField.ENRICHMENT_SCORE,
      label: getLocaleText(
        "helpers.mentions.sort_options.risk_enrichment_react_score"
      ),
    });

    possibilities.push({
      options: [
        {
          field: SortOptionField.ENRICHMENT_FAKENESS,
          key: SortOptionField.ENRICHMENT_FAKENESS,
          label: getLocaleText(
            "helpers.mentions.sort_options.react_enrichment_aggregate_fakeness"
          ),
        },
        {
          field: SortOptionField.ENRICHMENT_EMOTIONALITY,
          key: SortOptionField.ENRICHMENT_EMOTIONALITY,
          label: getLocaleText(
            "helpers.mentions.sort_options.react_enrichment_aggregate_emotionality"
          ),
        },
        {
          field: SortOptionField.ENRICHMENT_HARMFUL,
          key: SortOptionField.ENRICHMENT_HARMFUL,
          label: getLocaleText(
            "helpers.mentions.sort_options.react_enrichment_aggregate_harmful"
          ),
        },
        {
          field: SortOptionField.ENRICHMENT_SPAM,
          key: SortOptionField.ENRICHMENT_SPAM,
          label: getLocaleText(
            "helpers.mentions.sort_options.react_enrichment_aggregate_spam"
          ),
        },
      ],
    });
  }

  return possibilities.flatMap(({ options }) => options);
}

function getSocialSortPossibilities(): SortOption[] {
  return [
    {
      field: SortOptionField.TIMESTAMP,
      key: SortOptionField.TIMESTAMP,
      label: getLocaleText("helpers.mentions.sort_options.timestamp"),
    },
    {
      field: SortOptionField.LIKE_COUNT,
      key: SortOptionField.LIKE_COUNT,
      label: getLocaleText("helpers.mentions.sort_options.like_count"),
    },
    {
      field: SortOptionField.COMMENT_COUNT,
      key: SortOptionField.COMMENT_COUNT,
      label: getLocaleText("helpers.mentions.sort_options.comment_count"),
    },
    {
      field: SortOptionField.ENGAGEMENT_COUNT,
      key: SortOptionField.ENGAGEMENT_COUNT,
      label: getLocaleText("helpers.mentions.sort_options.engagement_count"),
    },
    {
      field: SortOptionField.SENTIMENT,
      key: SortOptionField.SENTIMENT,
      label: getLocaleText("helpers.mentions.sort_options.sentiment"),
    },
    {
      field: SortOptionField.SOCIAL_AUTHOR,
      key: SortOptionField.SOCIAL_AUTHOR,
      label: getLocaleText("helpers.mentions.sort_options.social_author"),
    },
    {
      field: SortOptionField.FOLLOWER_COUNT,
      key: SortOptionField.FOLLOWER_COUNT,
      label: getLocaleText("helpers.mentions.sort_options.follower_count"),
    },
  ];
}

export type MentionUpdates = {
  count?: number | null;
  error?: string;
  status: "done" | "error" | "in-progress" | "limit-reached";
  summary?: string;
  title?: string;
};

function updateMention(mention: Mention, updates: MentionUpdates): void {
  const { count = null, error, status, summary, title } = updates;

  Object.assign(mention, {
    ...(error && { summaryError: error }),
    ...(summary && { summary }),
    ...(title && { title }),
    summaryCount: count,
    summaryStatus: status,
  });
}

export async function summarise({
  folder,
  isDerBuilder = false,
  mention,
  specificationId,
  summaryType,
  updateMentionSummary = updateMention,
}: {
  folder?: OrganisationReportFolder;
  isDerBuilder?: boolean;
  mention: Mention;
  specificationId?: number;
  summaryType: OrganisationReportSummaryType;
  updateMentionSummary?: typeof updateMention;
}): Promise<void> {
  if (mention.medium === "Social") return;

  try {
    updateMentionSummary(mention, { status: "in-progress" });

    if (isDerBuilder && isMentionFinancialTimes(mention)) {
      ModalService.open("OrganisationReportSummaryModal", {
        events: {
          update: (changes: { summary: string; title: string }) => {
            updateMentionSummary(mention, { status: "done", ...changes });
          },
        },
        props: {
          isDerBuilder,
          mention,
        },
      });
    }

    const { data } = await OrganisationReportService.mentionSummary(
      mention,
      summaryType,
      isDerBuilder,
      specificationId
    );

    updateMentionSummary(mention, { status: "done", ...data });

    if (folder && isMentionWithRelatedArticle(mention)) {
      const relatedMention = folder.items.find(
        ({ id }) => id === mention.related_article.id
      );

      if (relatedMention && isOrganisationReportMention(relatedMention)) {
        updateMentionSummary(relatedMention, { status: "done", ...data });
      }
    }
  } catch (error: unknown) {
    if (ApiClient.isApiError(error) && error.response?.status === 429) {
      updateMentionSummary(mention, { status: "limit-reached" });
      ModalService.close("OrganisationReportSummaryModal");
    } else {
      updateMentionSummary(mention, {
        error:
          error instanceof ApiClientError
            ? error.response?.data?.message
            : undefined,
        status: "error",
      });
    }
  }
}

function sentimentRating(
  mention: Mention,
  stream: Stream
): { label: string; sentiment: Sentiment } {
  let rating = {
    sentiment: mention.sentiment,
  };

  const manualRating =
    mention.sentiment_ratings?.find(
      (mentionSentimentRating) =>
        mentionSentimentRating.origin_id === stream.id &&
        mentionSentimentRating.origin_type === "Stream"
    ) || mention.sentiment_ratings?.at(-1);

  if (
    manualRating &&
    (manualRating.origin_id === stream.id || !manualRating.origin_id)
  ) {
    rating = manualRating;
  }

  const option = sentimentOptions.find(
    (sentimentOption) =>
      isNumber(rating.sentiment) && sentimentOption.inRange(rating.sentiment)
  );

  const field = option ? option.field : "none";

  return {
    ...rating,
    label: getLocaleText(`sentiment.${field}`),
  };
}

/**
 * Broadcast mention types that should be bundled by time_bundle_key
 */
const BROADCAST_MENTION_TYPES = [
  "tv_caption",
  "tv_super",
  "tv_logo_appearance",
  "radio_clip",
] as const;

/**
 * Checks if a mention is a broadcast mention (TV/Radio)
 * @param mention - The mention to check
 * @returns true if the mention is a broadcast type
 */
function isBroadcastMention(mention: Pick<Mention, "type">): boolean {
  return BROADCAST_MENTION_TYPES.includes(
    mention.type as (typeof BROADCAST_MENTION_TYPES)[number]
  );
}

export {
  compositeIdToMention,
  compoundId,
  excerptsKeywords,
  excludedMediaItemSortFields,
  excludedTranscriptRequestSortFields,
  formattedMedium,
  getOrderOptions,
  getSocialSortPossibilities,
  getSortOptions,
  getSortPossibilities,
  isBroadcastMention,
  mentionKeywords,
  processMentions,
  resolveShortMentionType,
  sentimentRating,
  sortedSyndications,
  sortMentions,
  targetType,
};
